import { Cart } from "@/lib/shopify/types";
import { siteConfig } from "@/lib/seo-config";
import {
  bookLengthLabel,
  normalizePageCount,
  EXTENDED_BOOK_FLAG,
  EXTENDED_INNER_PAGES,
  PAGE_COUNT_ATTR,
  PAGE_COUNT_VISIBLE_ATTR,
  STANDARD_INNER_PAGES,
  type BookPageCount,
} from "@/lib/comix/book-length";

export const BAG_ENABLED = process.env.NEXT_PUBLIC_FEATURE_CART === "true";

// Mirrors the Shopify automatic discount ("50% off your 2nd book and up, same
// kind": printed + printed or digital + digital — formats never mix). The
// discount itself is applied by Shopify; these only drive the bag's copy, so
// if the Shopify admin setup changes, they must change with it.
// Shipping is intentionally NOT modelled here — it's calculated by Shopify at
// checkout, so the bag shows merchandise totals only.
export const BAG_DISCOUNT_PERCENT = 50;
/**
 * Line attribute marking "Buy a physical copy" of a book the customer already bought printed
 * and approved. The bag keeps it (it isn't a duplicate purchase), and order-paid prints it as
 * its own job with no approval step (src/lib/order-paid/reorder.ts).
 */
export const REORDER_ATTR = "_reorder";
export const isReorderLine = (line: { attributes?: { key: string; value: string }[] }) =>
  line.attributes?.some((a) => a.key === REORDER_ATTR && a.value === "1") ?? false;
/** Most copies of one book a shopper can add from the unlock page. */
export const MAX_BOOK_QUANTITY = 10;
/** Inverse of multiCopyCents: the full (undiscounted) price of `qty` copies, from what was charged. */
export function multiCopyListCents(chargedCents: number, qty: number): number {
  if (qty <= 1) return chargedCents;
  const guess = chargedCents / (1 + (qty - 1) / 2);
  for (let u = Math.floor(guess) - 2; u <= Math.ceil(guess) + 2; u++) {
    if (multiCopyCents(u, qty) === chargedCents) return u * qty;
  }
  return chargedCents;
}
/** Cents for `qty` copies of one book: the 1st at full price, each further copy at the discount. */
export function multiCopyCents(unitCents: number, qty: number): number {
  const discountedUnit = Math.round((unitCents * (100 - BAG_DISCOUNT_PERCENT)) / 100);
  return unitCents + Math.max(qty - 1, 0) * discountedUnit;
}

// Shopify's live automatic order discount ("25% off orders over $80") — separate from
// BAG_DISCOUNT_PERCENT above, which only drives the "2nd book" bag copy. Used where a
// price preview needs to reflect what this order discount will actually charge.
export const ORDER_DISCOUNT_THRESHOLD = 80;
export const ORDER_DISCOUNT_PERCENT = 25;
/** Cents for a subtotal of `subtotalCents`: 25% off once it reaches $80, flat otherwise. */
export function orderThresholdDiscountedCents(subtotalCents: number): number {
  if (subtotalCents < ORDER_DISCOUNT_THRESHOLD * 100) return subtotalCents;
  return Math.round((subtotalCents * (100 - ORDER_DISCOUNT_PERCENT)) / 100);
}
/** Inverse of orderThresholdDiscountedCents: the full (undiscounted) price for `qty`
 *  copies, from what was actually charged. */
export function orderThresholdListCents(chargedCents: number, qty: number): number {
  if (qty <= 0) return chargedCents;
  const guess = Math.round(chargedCents / (1 - ORDER_DISCOUNT_PERCENT / 100));
  for (let total = Math.max(0, guess - 5); total <= guess + 5; total++) {
    if (orderThresholdDiscountedCents(total) === chargedCents) return total;
  }
  return chargedCents;
}

export const DIGITAL_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_DIGITAL_VARIANT_ID ||
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_VARIANT_ID ||
  "";
export const PRINTED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PRINTED_VARIANT_ID ||
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_VARIANT_ID ||
  "";
// Discounted printed book offered as a post-purchase upsell ("Get it printed
// too!" on /comix/unlock). Still fulfilled as a printed book (_product stays
// "printed"); only the variant/price differ from the standalone printed book.
export const UPSELL_PRINTED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PHYSICAL_20_VARIANT_ID ||
  PRINTED_VARIANT_ID ||
  "";

// Extended edition (30 pages incl. covers, +$10) — separate Shopify variants so checkout
// charges the right price and the order shows which book was bought. Unset = the length
// picker isn't offered and every book is the standard 14-page one.
export const DIGITAL_EXTENDED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_DIGITAL_EXTENDED_VARIANT_ID || "";
export const PRINTED_EXTENDED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PRINTED_EXTENDED_VARIANT_ID || "";
// No fallback to the full-price extended printed variant: without its own discounted
// variant, an extended book's upsell uses the standard upsell variant (undercharges by the
// $10 rather than charging the full $50 against a "$20" button).
export const UPSELL_PRINTED_EXTENDED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PHYSICAL_20_EXTENDED_VARIANT_ID || "";

/** Post-purchase upgrade: owns the Standard (14-page) digital book, pays the $10 difference
 *  to extend it to 30 pages — no physical component, no re-sold base price. "_product" on
 *  this cart line is COMIX_PRODUCT_DIGITAL_UPGRADE, not "digital" (keeps upgrade revenue
 *  distinct from original-purchase revenue in Order rows / Amplitude). */
export const DIGITAL_UPGRADE_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_DIGITAL_UPGRADE_VARIANT_ID || "";
export const DIGITAL_UPGRADE_AVAILABLE = EXTENDED_BOOK_FLAG && !!DIGITAL_UPGRADE_VARIANT_ID;
/** `_product` value for the digital-only post-purchase length upgrade (see above). */
export const COMIX_PRODUCT_DIGITAL_UPGRADE = "digital-upgrade";

/** Offer the 14/30-page choice — behind NEXT_PUBLIC_COMIX_EXTENDED_BOOK. Without the extended
 *  Shopify variants (e.g. local testing) an extended line falls back to the standard variant's
 *  price — see comixVariantId — so only turn the flag on in production once they exist. */
export const EXTENDED_BOOK_ENABLED = EXTENDED_BOOK_FLAG;

export type ComixVariantKind = "digital" | "printed" | "upsell-printed";

/** The Shopify variant for a format + length. Falls back to the standard variant when the
 *  extended one isn't configured, so a misconfigured env can never block a sale. */
export function comixVariantId(kind: ComixVariantKind, pageCount: BookPageCount = STANDARD_INNER_PAGES): string {
  const extended = EXTENDED_BOOK_FLAG && pageCount === EXTENDED_INNER_PAGES;
  if (kind === "digital") return (extended && DIGITAL_EXTENDED_VARIANT_ID) || DIGITAL_VARIANT_ID;
  if (kind === "upsell-printed") return (extended && UPSELL_PRINTED_EXTENDED_VARIANT_ID) || UPSELL_PRINTED_VARIANT_ID;
  return (extended && PRINTED_EXTENDED_VARIANT_ID) || PRINTED_VARIANT_ID;
}

/** Length of a cart line — lines added before this feature have no attribute → standard. */
export function linePageCount(line: { attributes?: { key: string; value: string }[] }): BookPageCount {
  return normalizePageCount(lineAttr(line, PAGE_COUNT_ATTR));
}

/** Attributes that record the picked length on a cart line (hidden + customer-visible). */
export function pageCountAttributes(pageCount: BookPageCount): { key: string; value: string }[] {
  return [
    { key: PAGE_COUNT_ATTR, value: String(pageCount) },
    { key: PAGE_COUNT_VISIBLE_ATTR, value: bookLengthLabel(pageCount) },
  ];
}

/** Replace a line's length attributes (used when the length changes inside the bag). */
export function withPageCount(
  attributes: { key: string; value: string }[] | undefined,
  pageCount: BookPageCount,
): { key: string; value: string }[] {
  return [
    ...(attributes || []).filter((a) => a.key !== PAGE_COUNT_ATTR && a.key !== PAGE_COUNT_VISIBLE_ATTR),
    ...pageCountAttributes(pageCount),
  ];
}

export function lineAttr(
  line: { attributes?: { key: string; value: string }[] },
  key: string,
): string | undefined {
  return line.attributes?.find((a) => a.key === key)?.value;
}

export function isDigitalLine(line: {
  attributes?: { key: string; value: string }[];
}): boolean {
  return lineAttr(line, "_product") === "digital";
}

export type BagKind = "printed" | "digital";

export interface BagTotals {
  /** Undiscounted merchandise subtotal — drives the promo progress bar. */
  subtotal: number;
  /** Merchandise total after Shopify's automatic discount. */
  discountedSubtotal: number;
  discountActive: boolean;
  /**
   * Formats with exactly one book in the bag — adding a 2nd of that same
   * format is what unlocks the discount. Empty once every format present
   * already has a 2nd book.
   */
  upsellKinds: BagKind[];
  /** Formats with 2+ books in the bag — the ones the discount currently applies to. */
  discountKinds: BagKind[];
  /** Order (merchandise) savings from the automatic discount. */
  totalSavings: number;
  /** Merchandise total shown in the bag. Shipping is added by Shopify at checkout. */
  total: number;
  totalBeforeDiscount: number;
}

export function computeBagTotals(cart: Cart): BagTotals {
  const subtotal = parseFloat(cart.cost.subtotalAmount.amount || "0");
  const discountedSubtotal = parseFloat(cart.cost.totalAmount.amount || "0");
  const discountActive = discountedSubtotal < subtotal - 0.001;

  const qty: Record<BagKind, number> = { printed: 0, digital: 0 };
  for (const line of cart.lines) {
    qty[isDigitalLine(line) ? "digital" : "printed"] += line.quantity;
  }
  const upsellKinds = (["printed", "digital"] as const).filter((k) => qty[k] === 1);
  const discountKinds = (["printed", "digital"] as const).filter((k) => qty[k] >= 2);

  return {
    subtotal,
    discountedSubtotal,
    discountActive,
    upsellKinds,
    discountKinds,
    totalSavings: subtotal - discountedSubtotal,
    total: discountedSubtotal,
    totalBeforeDiscount: subtotal,
  };
}

/**
 * Per-line savings from "50% off your 2nd book and up, same kind only". Within each
 * kind (printed / digital) the single most expensive book stays full price and every
 * other book is BAG_DISCOUNT_PERCENT off. Returns line id -> savings in dollars.
 */
export function computeLineDiscounts(cart: Cart, actualSavings?: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const kind of ["printed", "digital"] as const) {
    const lines = cart.lines.filter((l) => (isDigitalLine(l) ? "digital" : "printed") === kind);
    const units = lines.flatMap((l) => {
      const unit = parseFloat(l.cost.subtotalAmount?.amount || "0") / Math.max(l.quantity, 1);
      return Array.from({ length: l.quantity }, () => ({ id: l.id, unit }));
    });
    units.sort((a, b) => b.unit - a.unit);
    units.slice(1).forEach((u) => {
      out[u.id] = (out[u.id] ?? 0) + (u.unit * BAG_DISCOUNT_PERCENT) / 100;
    });
  }
  if (actualSavings === undefined) return out;
  // Shopify decides what is really discounted: show nothing while it isn't applying a
  // discount, and scale ours so the lines always add up to the savings in the total.
  const raw = Object.values(out).reduce((n, v) => n + v, 0);
  if (actualSavings <= 0.001 || raw <= 0) return {};
  const ids = Object.keys(out);
  const cents: Record<string, number> = {};
  let left = Math.round(actualSavings * 100);
  ids.forEach((id, i) => {
    cents[id] = i === ids.length - 1 ? left : Math.min(left, Math.round((out[id] / raw) * actualSavings * 100));
    left -= cents[id];
  });
  return Object.fromEntries(ids.map((id) => [id, cents[id] / 100]));
}

/**
 * Per-line share (in cents) of an order-level discount, split proportionally to each
 * line's own subtotal — model-agnostic, so it reconciles correctly against whatever
 * Shopify's real cart total actually charged (the $80/25%-off order discount today,
 * or any other order-level discount later) rather than assuming same-kind pairing.
 * Returns cart line id -> cents of savings; always adds up to exactly `savingsCents`.
 */
export function proportionalLineDiscountCents(cart: Cart, savingsCents: number): Record<string, number> {
  if (savingsCents <= 0 || !cart.lines.length) return {};
  const subtotalCents = Math.round(parseFloat(cart.cost.subtotalAmount.amount || "0") * 100);
  if (subtotalCents <= 0) return {};
  const out: Record<string, number> = {};
  let left = savingsCents;
  cart.lines.forEach((line, i) => {
    const lineCents = Math.round(parseFloat(line.cost.subtotalAmount?.amount || "0") * 100);
    const share = Math.round((lineCents / subtotalCents) * savingsCents);
    out[line.id] = i === cart.lines.length - 1 ? left : Math.min(left, share);
    left -= out[line.id];
  });
  return out;
}

/**
 * Builds the Shopify line-item attributes for a comix run. The webhook
 * (order-paid) and the bag UI both depend on these keys — keep in sync there.
 */
export function buildComixLineAttributes(opts: {
  run: {
    sessionId?: string | null;
    userEmail?: string | null;
    generatedImages?: string[];
    config?: Record<string, any> | null;
  };
  runId: string;
  product: string;
  fallbackSessionId?: string;
  characterPhotoUrls?: string[];
  /** Picked book length (inner pages). Defaults to standard. */
  pageCount?: BookPageCount;
}): { key: string; value: string }[] {
  const { run, runId, product, fallbackSessionId, characterPhotoUrls = [], pageCount = STANDARD_INNER_PAGES } = opts;
  const config = (run.config as Record<string, any>) || {};
  const coverImage = config.coverImage || config.finishedBook?.coverImage;
  const allImages = [
    ...(coverImage ? [coverImage] : []),
    ...(run.generatedImages || []),
  ];
  const activeSessionId = run.sessionId || fallbackSessionId || "";
  const storyId = config.storyId || "";
  const storyTitle = config.storyHeadline || config.storyTemplate || "";
  const baseUrl = siteConfig.url;
  const previewUrl = `${baseUrl}/comix/preview?sessionId=${activeSessionId}&storyId=${storyId}`;

  return [
    { key: "_generated", value: allImages.join(",") },
    { key: "_sessionId", value: activeSessionId },
    { key: "_runId", value: runId },
    { key: "_productType", value: "comix" },
    { key: "_product", value: product },
    { key: "_previewUrl", value: previewUrl },
    ...(run.userEmail ? [{ key: "_customerEmail", value: run.userEmail }] : []),
    ...(storyId ? [{ key: "_storyId", value: storyId }] : []),
    ...(characterPhotoUrls.length > 0
      ? [{ key: "_characterPhotos", value: characterPhotoUrls.join(",") }]
      : []),
    ...(storyTitle ? [{ key: "_title", value: storyTitle }] : []),
    // Underscore keys are hidden by Shopify; this one is shown under the line item at checkout.
    ...(storyTitle ? [{ key: "Book title", value: storyTitle }] : []),
    ...(coverImage ? [{ key: "_coverImage", value: coverImage }] : []),
    // Only with the feature on — off, cart lines stay exactly as they were before it.
    ...(EXTENDED_BOOK_FLAG ? pageCountAttributes(pageCount) : []),
  ];
}

/**
 * Cart line attributes are writable by anyone holding the public Storefront
 * token, so URLs read from them must never reach href/src unsanitized
 * (blocks javascript:, data:, etc.).
 */
export function safeHttpUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return url;
    }
  } catch {
    // not an absolute URL
  }
  return undefined;
}

export function formatUsd(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}
